import { api_configuration } from "../../common";
import cs = api_configuration.types.client_state.alan_api;
import api_collation_table = api_configuration.libraries.gui_definition_lib.types.collation_table.alan_api;
import serializerNumber = require("../state 2 interface/serializeNumber");
import serializeNumber = serializerNumber.serializeNumber;
import { updateEntriesSelection } from "./selection";
import { on_node_update } from "./events";
import DestructorTree from "../DestructorTree";
import {
	addResultEntry,
	removeResultEntry,
	hasResult,
	getIndexedResultEntries,
	localQueryEntry,
	getQueryEntryIndexForEntry,
	setSubscribed,
	getPropertyForCell,
} from "./Query";
import walkNodePath from "../navigation/walkNodePath";
import * as Cell from "../state/Cell";
import { iReference } from "../state/Reference";
import * as Entry from "../state/Entry";
import supportsSelfReference from "../state/supportsSelfReference";
import * as ClientState from "../state/ClientState";
import * as Status from "../Status";
import { getTranslation } from "./phrases";
import * as reQuery from "../state 2 interface/reQuery";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import * as Query from "../state/Query";

function getCharacterAlternatives(character: api_collation_table.Ccharacters): Record<string, Record<never, never>> {
	const type = character.properties.type.state;
	switch (type.name) {
		case "alternative":
			return global.collation_table_inverse[type.node.properties.for_.inferences.for_().parent.key];
		case "primary":
			return global.collation_table_inverse[type.node.parent.key];
	}
}

function createRegexFromString(value: string): string {
	let regex = "";
	if (value.length === 0) return regex;

	for (let i = 0, character: string; (character = value[i]), i < value.length; i++) {
		if (character === "*") {
			regex = regex + ".*";
		} else {
			const entry: api_collation_table.Ccharacters = global.collation_table.properties.characters.get(character);
			if (entry !== undefined) {
				regex = regex + "[" + Object.keys(getCharacterAlternatives(entry)).join() + "]";
			} else {
				regex = regex + character;
			}
		}
	}
	return regex;
}

function createRegexFromFilter(filter: cs.Cyes__has_filter__text) {
	switch (filter.properties.operation.state.name) {
		case "contains":
			return "^.*" + createRegexFromString(filter.properties.value) + ".*$";
		case "starts with":
			return "^" + createRegexFromString(filter.properties.value) + ".*$";
		case "ends with":
			return "^.*" + createRegexFromString(filter.properties.value) + "$";
		case "equals":
			return "^" + createRegexFromString(filter.properties.value) + "$";
	}
}

function filter_text(filter: cs.Cyes__has_filter__text, value: string) {
	return value.match(createRegexFromFilter(filter)) !== null;
}

function checkFilter(node: cs.Cnode, query: cs.Cquery__client_state): boolean {
	let valid = true;
	query.properties.columns.forEach(function (column) {
		column.properties.type.switch({
			text: function (text) {
				text.properties.has_filter.switch({
					no: undefined,
					yes: function (has_filter) {
						has_filter.properties.filter_set.switch({
							no: undefined,
							yes: function () {
								const text_context = walkNodePath(text.parent.key.ref.properties.path, node);
								valid =
									valid &&
									(text_context?.properties.attributes.switchOnEntryExists(
										text.inferences.text_type().properties.text.entry,
										($) => {
											const text_property = $.properties.type.cast("property").properties.type.cast("text");
											return text_property.properties.is_set.switch({
												no: false,
												yes: (is_set) => filter_text(has_filter, is_set.properties.text),
											});
										},
										() => false
									) ??
										true);
							},
						});
					},
				});
			},
			file: undefined,
			number: function (number) {
				number.properties.has_filter.switch({
					no: undefined,
					yes: function (has_filter) {
						has_filter.properties.filter_set.switch({
							no: undefined,
							yes: function () {
								has_filter.properties.criteria_is_valid.switch({
									no: undefined,
									yes: function (criteria_valid) {
										valid =
											valid &&
											(walkNodePath(
												number.parent.key.ref.properties.path,
												node
											)?.properties.attributes.switchOnEntryExists(
												number.inferences.number_type().properties.number.ref.parent.parent.key.ref.key,
												($) => {
													$.properties.type
														.cast("property")
														.properties.type.cast("number")
														.properties.is_set.switch({
															no: () => false,
															yes: (is_set) =>
																has_filter.properties.operator_selected.switch({
																	no: () => undefined,
																	yes: (operator_selected) =>
																		operator_selected.properties.operator.switch({
																			"smaller than": () =>
																				is_set.properties.number.value < criteria_valid.properties.criteria.value,
																			"smaller than or equal to": () =>
																				is_set.properties.number.value <= criteria_valid.properties.criteria.value,
																			"equal to": () =>
																				is_set.properties.number.value === criteria_valid.properties.criteria.value,
																			"greater than": () =>
																				is_set.properties.number.value > criteria_valid.properties.criteria.value,
																			"greater than or equal to": () =>
																				is_set.properties.number.value >= criteria_valid.properties.criteria.value,
																		}),
																}),
														});
												},
												() => undefined
											) ??
												true);
									},
								});
							},
						});
					},
				});
			},
			"state group": function (state_group) {
				state_group.properties.has_filter.switch({
					no: undefined,
					yes: function (has_filter) {
						has_filter.properties.filter_enabled.switch({
							no: undefined,
							yes: function () {
								valid =
									valid &&
									(walkNodePath(
										state_group.parent.key.ref.properties.path,
										node
									)?.properties.attributes.switchOnEntryExists(
										state_group.inferences.state_group_type().properties.state_group.ref.parent.parent.key.ref.key,
										($) =>
											$.properties.type
												.cast("property")
												.properties.type.cast("state group")
												.properties.is_set.switch({
													no: () => false,
													yes: (is_set) =>
														has_filter.properties.states
															.get(is_set.properties.state.entry)!
															.properties.is_selected.switch({
																no: () => false,
																yes: () => undefined,
															}),
												}),
										() => undefined
									) ??
										true);
							},
						});
					},
				});
			},
			widget: undefined,
		});
	});
	return valid;
}

function updateEntryFromCollectionEntry(target: cs.Centries__yes__has_result, source: cs.Centries__collection) {
	const is_new_entry = source.properties.location.switch({
		local: true,
		remote: false,
	});

	Query.storeEntryId(target.properties.entry_status, createIdPathBuilderFromNode(source.properties.node));

	for (const cell of target.properties.cells.values()) {
		const cell_type = cell.properties.type.state;
		switch (cell_type.name) {
			case "text": {
				const text_cell = cell_type.node;
				const text_property = getPropertyForCell(source, text_cell);
				if (text_property) {
					switch (text_property.properties.is_set.state.name) {
						case "yes": {
							const property_is_set = text_property.properties.is_set.state.node;
							const source_value = property_is_set.properties.text;
							Cell.updateText(text_cell, source_value);
							break;
						}
						case "no":
							if (is_new_entry && text_cell.properties.is_set.state.name === "yes") {
								text_cell.properties.is_set.set("no", {});
							}
							break;
					}
				}
				break;
			}
			case "number": {
				const number_cell = cell_type.node;
				const number_property = getPropertyForCell(source, number_cell);
				if (number_property) {
					switch (number_property.properties.is_set.state.name) {
						case "yes": {
							const property_is_set = number_property.properties.is_set.state.node;
							const source_value = property_is_set.properties.number.value;
							const source_textual_representation = serializeNumber(
								source_value,
								serializerNumber.getDecimalsFromNumericalTypeProperties(
									number_cell.properties.numerical_type_properties
								),
								number_cell.inferences.column_type().inferences.number_type().properties.number.ref.properties
									.numerical_type.ref.key.ref
							);
							Cell.updateNumber(number_cell, source_value, source_textual_representation);
							break;
						}
						case "no":
							if (is_new_entry && number_cell.properties.is_set.state.name === "yes") {
								number_cell.properties.is_set.set("no", {});
							}
							break;
					}
				}
				break;
			}
			case "file": {
				const file_cell = cell_type.node;
				const file_property = getPropertyForCell(source, file_cell);
				if (file_property !== undefined) {
					switch (file_property.properties.backend_known.state.name) {
						case "no":
							break;
						case "yes": {
							const $ = file_property.properties.backend_known.state.node;
							switch ($.properties.is_readable.state.name) {
								case "no":
									break;
								case "yes": {
									const readable_file = $.properties.is_readable.state.node;
									Cell.updateFile(file_cell, readable_file.properties.token, readable_file.properties.extension);
									break;
								}
							}
							break;
						}
					}
				}
				break;
			}
			case "state group": {
				const state_group_cell = cell_type.node;
				const state_group_property = getPropertyForCell(source, state_group_cell);
				if (state_group_property) {
					switch (state_group_property.properties.is_set.state.name) {
						case "yes": {
							const property_is_set = state_group_property.properties.is_set.state.node;
							const source_value = property_is_set.properties.state.entry;
							Cell.updateStategroup(state_group_cell, getTranslation(source_value));
							break;
						}
						case "no":
							if (is_new_entry && state_group_cell.properties.is_set.state.name === "yes") {
								state_group_cell.properties.is_set.set("no", {});
							}
							break;
					}
				}
				break;
			}
			case "widget":
				break;
		}
	}
}

export default function updateQueryFromCollection(query: cs.Cquery__client_state): void {
	switch (query.properties.use_for.state.name) {
		case "query": {
			switch (query.properties.entries_are_editable.state.name) {
				case "yes": {
					const editable = query.properties.entries_are_editable.state.node;
					switch (editable.properties.can_create_entry.state.name) {
						case "yes": {
							const can_create_entry = editable.properties.can_create_entry.state.node;
							const destructor = setSubscribed(query);
							if (destructor) {
								const collection = can_create_entry.properties.in_.cast("query").properties.collection.ref;
								subscribeToCollection(query, collection, destructor);
							}
							break;
						}
						case "no":
							break;
					}
					break;
				}
				case "no":
					break;
			}
			break;
		}
		case "candidates": {
			const for_candidates = query.properties.use_for.state.node;
			const destructor = setSubscribed(query);
			if (destructor) {
				const reference = for_candidates.inferences.reference_property().parent;
				const collection_context = iReference(reference).collectionContext;
				let c_destructor: DestructorTree | undefined;
				const subscription = collection_context.subscribe({
					update: (collection) => {
						reQuery.candidates(reference);
						if (Status.isResolved(collection)) {
							c_destructor?.destroy();
							c_destructor = destructor.addChild();
							const blacklist = new Set<string>();
							switch (reference.parent.properties.is_key.state.name) {
								case "yes": {
									const key_property = reference.parent.properties.is_key.state.node;
									getLocalEntriesBlacklist(key_property, blacklist);
									break;
								}
								case "no":
									break;
							}
							const self_reference_allowed = supportsSelfReference(
								reference.inferences.is_navigable().properties.expression
							);
							if (!self_reference_allowed) {
								const self_entity = reference.component_root.input.entity();
								switch (self_entity.variant.name) {
									case "root":
										break;
									case "entry": {
										const self_value = Entry.getKeyValue(self_entity.variant.definition);
										if (self_value !== undefined) {
											blacklist.add(self_value);
										}
										break;
									}
								}
							}
							subscribeToCollection(query, collection, c_destructor, blacklist);
						}
					},
				});
				destructor.onDestroy(() => collection_context.unsubscribe(subscription));
			}
			break;
		}
	}
}

function getLocalEntriesBlacklist(this_key_property: cs.Cyes__is_key, blacklist: Set<string>): Set<string> {
	const collection = this_key_property.inferences.entry().parent;
	for (const entry of collection.properties.entries.values()) {
		const key = Entry.getKeyValue(entry);
		if (key === undefined) break;
		switch (entry.properties.location.state.name) {
			case "remote":
				break;
			case "local": {
				const that_key_property = entry.properties.key.ref;
				if (that_key_property !== this_key_property.parent) {
					blacklist.add(key);
				}
				break;
			}
			default:
				unexpectedState(entry.properties.location.state);
		}
	}
	return blacklist;
}

const updateQueue = new Map<cs.Cquery__client_state, { collection: cs.Ccollection; blacklist: Set<string> }>();
let update_queued = false;

function updateQueries() {
	if (!update_queued) {
		update_queued = true;
		requestAnimationFrame(() => {
			ClientState.onTransactionEnds(() => {
				update_queued = false;
				for (const [query, { collection, blacklist }] of updateQueue) {
					if (query.destroyed || collection.destroyed) continue;
					updateEntries(query, collection, blacklist);
				}
				updateQueue.clear();
			});
		});
	}
}

function subscribeToCollection(
	query: cs.Cquery__client_state,
	collection: cs.Ccollection,
	destructor: DestructorTree,
	blacklist: Set<string> = new Set<string>()
): void {
	destructor.onDestroy(() => updateQueue.delete(query));

	destructor.subscribeToStategroup(query.properties.has_result, (has_result_destructor) => {
		switch (query.properties.has_result.state.name) {
			case "yes": {
				const result = query.properties.has_result.state.node;
				has_result_destructor.subscribeToCollection(result.properties.entries, {
					on_change: () => {
						updateQueue.set(query, { collection, blacklist });
						updateQueries();
					},
				});
				break;
			}
			case "no":
				break;
		}
	});

	destructor.subscribeToCollection(
		collection.properties.entries,
		{
			on_add: (entry, destructor) => {
				const node_subscription = on_node_update(entry.properties.node, () => {
					updateQueue.set(query, { collection, blacklist });
					updateQueries();
				});
				destructor.onDestroy(() => {
					node_subscription.unsubscribe();
				});
				updateQueue.set(query, { collection, blacklist });
				updateQueries();
			},
		},
		/*init=*/ true
	);
}

function localOrModified(entry: cs.Centries__collection): boolean {
	const is_local = entry.properties.node.properties.location.switch({
		local: true,
		remote: false,
	});
	const is_modified = entry.properties.node.properties.modified.switch({
		no: false,
		yes: true,
	});
	return is_local || is_modified;
}

function updateEntries(query: cs.Cquery__client_state, collection: cs.Ccollection, blacklist: Set<string>) {
	const has_result = hasResult(query);

	const entries_indexed = getIndexedResultEntries(has_result);
	for (const entry of collection.properties.entries.values()) {
		const entry_index = getQueryEntryIndexForEntry(query, entry);
		if (entry_index !== undefined) {
			const query_entry = entries_indexed.get(entry_index);
			const key = Entry.getKeyValue(entry);
			const blacklisted = key !== undefined ? blacklist.has(key) : false;
			const filtered = blacklisted || (localOrModified(entry) && !checkFilter(entry.properties.node, query));
			if (query_entry) {
				if (filtered) {
					has_result.set_total_amount_of_entries(has_result.properties.total_amount_of_entries.value - 1);
					removeResultEntry(query_entry);
				} else {
					updateEntryFromCollectionEntry(query_entry, entry);
				}
			} else {
				if (!filtered && localOrModified(entry)) {
					has_result.set_total_amount_of_entries(has_result.properties.total_amount_of_entries.value + 1);
					const query_entry = addResultEntry(has_result, localQueryEntry(entry));
					entry.onDestroy(() => {
						if (query_entry.destroyed) return;
						removeResultEntry(query_entry);
						reQuery.query(query);
					});
					updateEntryFromCollectionEntry(query_entry, entry);
				}
			}
		}
	}
	updateEntriesSelection(query);
}
